import React, { useState, useEffect } from 'react'
import { ArrowLeft } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useToast } from '../hooks/useToast'
import api from '../services/api'

interface Integration {
  id: number
  name: string
  provider: string
  provider_display: string
  status: 'connected' | 'disconnected' | 'error'
  configuration: {
    api_url?: string
    api_key?: string
    instance_name?: string
  }
  last_test?: string
  created_at: string
}

interface QRCodeData {
  qrcode: string
  instance: string
}

interface Pipeline {
  id: number
  name: string
}

interface Stage {
  id: number
  name: string
  pipeline: number
}

const Integrations = () => {
  const navigate = useNavigate()
  const { showToast } = useToast()
  const [integrations, setIntegrations] = useState<Integration[]>([])
  const [loading, setLoading] = useState(true)
  const [showWhatsAppModal, setShowWhatsAppModal] = useState(false)
  const [qrCode, setQRCode] = useState<QRCodeData | null>(null)
  const [connectingWhatsApp, setConnectingWhatsApp] = useState(false)
  const [instanceName, setInstanceName] = useState('')
  const [checkingConnection, setCheckingConnection] = useState(false)
  const [webhookUrl, setWebhookUrl] = useState('')
  const [pipelines, setPipelines] = useState<Pipeline[]>([])
  const [stages, setStages] = useState<Stage[]>([])
  const [selectedPipeline, setSelectedPipeline] = useState<number | null>(null)
  const [selectedStage, setSelectedStage] = useState<number | null>(null)
  const [showConfigModal, setShowConfigModal] = useState(false)
  const [configIntegration, setConfigIntegration] = useState<Integration | null>(null)

  // Evolution API Credentials
  const EVOLUTION_API_URL = 'https://evolution.althub.app'
  const EVOLUTION_API_KEY = '429683C4C977415CAAFCCE10F7D57E11'
  
  // URL padrão do webhook (aponta para backend Railway em produção, ngrok em localhost)
  const DEFAULT_WEBHOOK_URL = window.location.hostname === 'localhost' 
    ? 'https://jubilantly-unobligated-eun.ngrok-free.dev/api/v1/messaging/webhook/evolution/'
    : 'https://flipr-crm-production.up.railway.app/api/v1/messaging/webhook/evolution/'

  useEffect(() => {
    loadIntegrations()
    loadPipelines()
  }, [])

  const loadPipelines = async () => {
    try {
      const response = await api.get('/pipelines/pipelines/')
      const data = Array.isArray(response.data) ? response.data : response.data.results || []
      setPipelines(data)
    } catch (error) {
      console.error('Erro ao carregar pipelines:', error)
    }
  }

  const loadStages = async (pipelineId: number) => {
    try {
      const response = await api.get(`/pipelines/stages/?pipeline=${pipelineId}`)
      const data = Array.isArray(response.data) ? response.data : response.data.results || []
      setStages(data)
    } catch (error) {
      console.error('Erro ao carregar stages:', error)
    }
  }

  const loadIntegrations = async () => {
    try {
      const response = await api.get('/messaging/integrations/')
      // Garantir que sempre seja um array
      const data = Array.isArray(response.data) ? response.data : response.data.results || []
      setIntegrations(data)
    } catch (error) {
      console.error('Erro ao carregar integrações:', error)
      showToast('Erro ao carregar integrações', 'error')
      setIntegrations([]) // Garantir array vazio em caso de erro
    } finally {
      setLoading(false)
    }
  }

  const handleConnectWhatsApp = async () => {
    if (!instanceName.trim()) {
      showToast('Digite um nome para a instância', 'error')
      return
    }
    
    if (!webhookUrl.trim()) {
      showToast('Digite a URL do webhook', 'error')
      return
    }

    setConnectingWhatsApp(true)
    try {
      // 1. Criar instância na Evolution API (formato WhatsApp)
      const createResponse = await fetch(`${EVOLUTION_API_URL}/instance/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apikey': EVOLUTION_API_KEY
        },
        body: JSON.stringify({
          instanceName: instanceName,
          integration: 'WHATSAPP-BAILEYS'
        })
      })

      if (!createResponse.ok) {
        const errorData = await createResponse.json()
        throw new Error(errorData.message || 'Erro ao criar instância')
      }

      const createData = await createResponse.json()
      
      // 2. Conectar e gerar QR Code
      const connectResponse = await fetch(`${EVOLUTION_API_URL}/instance/connect/${instanceName}`, {
        method: 'GET',
        headers: {
          'apikey': EVOLUTION_API_KEY
        }
      })

      if (!connectResponse.ok) {
        throw new Error('Erro ao gerar QR Code')
      }

      const connectData = await connectResponse.json()
      
      // QR Code pode vir em diferentes formatos
      const qrcodeData = connectData.qrcode?.code || connectData.qrcode?.base64 || connectData.base64 || connectData.code
      
      if (!qrcodeData) {
        throw new Error('QR Code não foi gerado')
      }
      
      setQRCode({
        qrcode: qrcodeData,
        instance: instanceName
      })

      // Verificar conexão periodicamente
      startConnectionCheck(instanceName)

      showToast('QR Code gerado! Escaneie com seu WhatsApp', 'success')
    } catch (error: any) {
      console.error('Erro ao conectar WhatsApp:', error)
      showToast(error.message || 'Erro ao conectar WhatsApp', 'error')
      setShowWhatsAppModal(false)
    } finally {
      setConnectingWhatsApp(false)
    }
  }

  const startConnectionCheck = (instance: string) => {
    setCheckingConnection(true)
    
    const interval = setInterval(async () => {
      try {
        const response = await fetch(`${EVOLUTION_API_URL}/instance/connectionState/${instance}`, {
          headers: {
            'apikey': EVOLUTION_API_KEY
          }
        })

        const data = await response.json()
        
        if (data.state === 'open' || data.instance?.state === 'open') {
          clearInterval(interval)
          setCheckingConnection(false)
          
          // Salvar integração no backend
          await saveIntegration(instance)
          
          // Configurar webhook
          await setupWebhook(instance)
          
          setShowWhatsAppModal(false)
          setQRCode(null)
          showToast('WhatsApp conectado com sucesso!', 'success')
          loadIntegrations()
        }
      } catch (error) {
        console.error('Erro ao verificar conexão:', error)
      }
    }, 3000) // Verificar a cada 3 segundos

    // Limpar após 2 minutos
    setTimeout(() => {
      clearInterval(interval)
      setCheckingConnection(false)
    }, 120000)
  }

  const saveIntegration = async (instance: string) => {
    try {
      await api.post('/messaging/integrations/', {
        name: `WhatsApp - ${instance}`,
        provider: 'evolution',
        provider_display: 'Evolution API',
        configuration: {
          api_url: EVOLUTION_API_URL,
          api_key: EVOLUTION_API_KEY,
          instance_name: instance,
          webhook_url: webhookUrl
        },
        default_pipeline: selectedPipeline,
        default_stage: selectedStage
      })
    } catch (error) {
      console.error('Erro ao salvar integração:', error)
    }
  }

  const setupWebhook = async (instance: string) => {
    try {
      const response = await fetch(`${EVOLUTION_API_URL}/webhook/set/${instance}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apikey': EVOLUTION_API_KEY
        },
        body: JSON.stringify({
          webhook: {
            enabled: true,
            url: webhookUrl,
            webhook_by_events: false,
            webhook_base64: false,
            events: [
              'MESSAGES_UPSERT',
              'MESSAGES_UPDATE',
              'CONNECTION_UPDATE'
            ]
          }
        })
      })
      
      if (response.ok) {
        const result = await response.json()
        console.log('Webhook configurado:', result)
        showToast('Webhook configurado com sucesso!', 'success')
      } else {
        throw new Error('Falha ao configurar webhook')
      }
    } catch (error) {
      console.error('Erro ao configurar webhook:', error)
      showToast('Aviso: Webhook pode não ter sido configurado', 'warning')
    }
  }

  const handleDisconnect = async (integration: Integration) => {
    if (!confirm('Deseja realmente desconectar esta integração?')) return

    try {
      // Deletar instância na Evolution API
      if (integration.configuration.instance_name) {
        await fetch(`${EVOLUTION_API_URL}/instance/delete/${integration.configuration.instance_name}`, {
          method: 'DELETE',
          headers: {
            'apikey': EVOLUTION_API_KEY
          }
        })
      }

      // Deletar do backend
      await api.delete(`/messaging/integrations/${integration.id}/`)
      
      showToast('Integração removida com sucesso', 'success')
      loadIntegrations()
    } catch (error) {
      console.error('Erro ao desconectar:', error)
      showToast('Erro ao desconectar integração', 'error')
    }
  }

  const handleUpdateConfig = async () => {
    if (!configIntegration) return

    try {
      await api.patch(`/messaging/integrations/${configIntegration.id}/`, {
        default_pipeline: selectedPipeline,
        default_stage: selectedStage
      })
      
      showToast('Configuração atualizada com sucesso!', 'success')
      setShowConfigModal(false)
      setConfigIntegration(null)
      setSelectedPipeline(null)
      setSelectedStage(null)
      setStages([])
      loadIntegrations()
    } catch (error) {
      console.error('Erro ao atualizar configuração:', error)
      showToast('Erro ao atualizar configuração', 'error')
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'connected':
        return 'var(--color-success-500)'
      case 'error':
        return 'var(--color-error-500)'
      default:
        return 'var(--text-tertiary)'
    }
  }

  const getStatusText = (status: string) => {
    switch (status) {
      case 'connected':
        return 'Conectado'
      case 'error':
        return 'Erro'
      default:
        return 'Desconectado'
    }
  }

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 'var(--space-12)' }}>
        <div className="spinner" />
        <p style={{ marginTop: 'var(--space-4)', color: 'var(--text-secondary)' }}>
          Carregando integrações...
        </p>
      </div>
    )
  }

  return (
    <div>
      {/* Back to Settings */}
      <button
        onClick={() => navigate('/settings')}
        style={{
          display: 'flex', alignItems: 'center', gap: 'var(--space-2)',
          background: 'none', border: 'none', cursor: 'pointer',
          color: 'var(--text-secondary)', fontSize: 'var(--text-sm)',
          padding: 'var(--space-1) 0', marginBottom: 'var(--space-4)',
          transition: 'color 0.2s'
        }}
        onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--primary-600)')}
        onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--text-secondary)')}
      >
        <ArrowLeft size={16} />
        Voltar para Configurações
      </button>

      {/* Header */}
      <div style={{ marginBottom: 'var(--space-6)' }}>
        <h1 style={{
          fontSize: 'var(--text-2xl)',
          fontWeight: 'var(--font-weight-bold)',
          color: 'var(--text-primary)',
          marginBottom: 'var(--space-2)'
        }}>
          Integrações
        </h1>
        <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-base)' }}>
          Conecte o FLIPR com WhatsApp, Instagram e outros serviços
        </p>
      </div>

      {/* Integration Cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
        gap: 'var(--space-4)'
      }}>
        {/* WhatsApp Card */}
        <div style={{
          background: 'var(--glass-bg-strong)',
          backdropFilter: 'var(--glass-blur)',
          WebkitBackdropFilter: 'var(--glass-blur)',
          border: '1px solid var(--glass-border)',
          borderRadius: 'var(--radius-2xl)',
          padding: 'var(--space-6)',
          transition: 'all var(--transition-fast)',
          boxShadow: 'var(--shadow-glass)'
        }}
        className="integration-card"
        >
          <div style={{ marginBottom: 'var(--space-4)' }}>
            <div style={{
              width: '48px',
              height: '48px',
              borderRadius: 'var(--radius-lg)',
              background: 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: 'var(--space-3)'
            }}>
              <svg width="28" height="28" viewBox="0 0 24 24" fill="white">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
              </svg>
            </div>
            <h3 style={{
              fontSize: 'var(--text-lg)',
              fontWeight: 'var(--font-weight-semibold)',
              color: 'var(--text-primary)',
              marginBottom: 'var(--space-1)'
            }}>
              WhatsApp
            </h3>
            <p style={{
              fontSize: 'var(--text-sm)',
              color: 'var(--text-secondary)',
              marginBottom: 'var(--space-3)'
            }}>
              Conecte seu WhatsApp Business para receber e enviar mensagens automaticamente
            </p>
          </div>

          {integrations.some(i => i.provider === 'evolution') ? (
            <>
              {integrations.filter(i => i.provider === 'evolution').map(integration => (
                <div key={integration.id} style={{
                  background: 'var(--bg-tertiary)',
                  padding: 'var(--space-3)',
                  borderRadius: 'var(--radius-lg)',
                  marginBottom: 'var(--space-3)'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--space-2)' }}>
                    <span style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-medium)' }}>
                      {integration.configuration.instance_name}
                    </span>
                    <span style={{
                      fontSize: 'var(--text-xs)',
                      fontWeight: 'var(--font-weight-medium)',
                      color: getStatusColor(integration.status),
                      display: 'flex',
                      alignItems: 'center',
                      gap: 'var(--space-1)'
                    }}>
                      <div style={{
                        width: '6px',
                        height: '6px',
                        borderRadius: '50%',
                        background: getStatusColor(integration.status)
                      }} />
                      {getStatusText(integration.status)}
                    </span>
                  </div>
                  <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
                    <button
                      onClick={async () => {
                        setConfigIntegration(integration)
                        
                        // Carregar valores atuais da integração
                        const response = await api.get(`/messaging/integrations/${integration.id}/`)
                        const integrationData = response.data
                        
                        if (integrationData.default_pipeline) {
                          setSelectedPipeline(integrationData.default_pipeline)
                          await loadStages(integrationData.default_pipeline)
                          
                          if (integrationData.default_stage) {
                            setSelectedStage(integrationData.default_stage)
                          }
                        }
                        
                        setShowConfigModal(true)
                      }}
                      style={{
                        flex: 1,
                        padding: 'var(--space-2)',
                        background: 'transparent',
                        border: '1px solid var(--color-primary-500)',
                        borderRadius: 'var(--radius-md)',
                        color: 'var(--color-primary-600)',
                        fontSize: 'var(--text-sm)',
                        fontWeight: 'var(--font-weight-medium)',
                        cursor: 'pointer',
                        transition: 'all var(--transition-fast)'
                      }}
                      className="config-btn"
                    >
                      ⚙️ Configurar
                    </button>
                    <button
                      onClick={() => handleDisconnect(integration)}
                      style={{
                        flex: 1,
                        padding: 'var(--space-2)',
                        background: 'transparent',
                        border: '1px solid var(--border-medium)',
                        borderRadius: 'var(--radius-md)',
                        color: 'var(--color-error-600)',
                        fontSize: 'var(--text-sm)',
                        fontWeight: 'var(--font-weight-medium)',
                        cursor: 'pointer',
                        transition: 'all var(--transition-fast)'
                      }}
                      className="disconnect-btn"
                    >
                      Desconectar
                    </button>
                  </div>
                </div>
              ))}
              <button
                onClick={() => {
                  setWebhookUrl(DEFAULT_WEBHOOK_URL)
                  setShowWhatsAppModal(true)
                }}
                style={{
                  width: '100%',
                  padding: 'var(--space-3)',
                  background: 'transparent',
                  border: '1px solid var(--color-success-500)',
                  borderRadius: 'var(--radius-lg)',
                  color: 'var(--color-success-600)',
                  fontSize: 'var(--text-sm)',
                  fontWeight: 'var(--font-weight-medium)',
                  cursor: 'pointer',
                  transition: 'all var(--transition-fast)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 'var(--space-2)'
                }}
                className="connect-more-btn"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="12" y1="5" x2="12" y2="19"/>
                  <line x1="5" y1="12" x2="19" y2="12"/>
                </svg>
                Adicionar Mais
              </button>
            </>
          ) : (
            <button
              onClick={() => {
                setWebhookUrl(DEFAULT_WEBHOOK_URL)
                setShowWhatsAppModal(true)
              }}
              style={{
                width: '100%',
                padding: 'var(--space-3)',
                background: 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)',
                border: 'none',
                borderRadius: 'var(--radius-lg)',
                color: 'white',
                fontSize: 'var(--text-sm)',
                fontWeight: 'var(--font-weight-semibold)',
                cursor: 'pointer',
                transition: 'all var(--transition-fast)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 'var(--space-2)'
              }}
              className="connect-btn"
            >
              Conectar WhatsApp
            </button>
          )}
        </div>

        {/* Coming Soon Cards */}
        {[
          { name: 'Instagram', color: '#E4405F', icon: 'instagram' },
          { name: 'Messenger', color: '#0084FF', icon: 'messenger' },
          { name: 'Telegram', color: '#0088CC', icon: 'telegram' }
        ].map(service => (
          <div key={service.name} style={{
            background: 'var(--glass-bg-strong)',
            backdropFilter: 'var(--glass-blur)',
            WebkitBackdropFilter: 'var(--glass-blur)',
            border: '1px solid var(--glass-border)',
            borderRadius: 'var(--radius-2xl)',
            padding: 'var(--space-6)',
            opacity: 0.6,
            position: 'relative',
            boxShadow: 'var(--shadow-glass)'
          }}>
            <div style={{
              position: 'absolute',
              top: 'var(--space-3)',
              right: 'var(--space-3)',
              background: 'var(--color-warning-100)',
              color: 'var(--color-warning-700)',
              padding: 'var(--space-1) var(--space-2)',
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--text-xs)',
              fontWeight: 'var(--font-weight-semibold)'
            }}>
              Em Breve
            </div>
            <div style={{
              width: '48px',
              height: '48px',
              borderRadius: 'var(--radius-lg)',
              background: service.color,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: 'var(--space-3)'
            }}>
              <span style={{ color: 'white', fontSize: 'var(--text-lg)' }}>📱</span>
            </div>
            <h3 style={{
              fontSize: 'var(--text-lg)',
              fontWeight: 'var(--font-weight-semibold)',
              color: 'var(--text-primary)',
              marginBottom: 'var(--space-1)'
            }}>
              {service.name}
            </h3>
            <p style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)' }}>
              Em breve você poderá conectar {service.name} ao FLIPR
            </p>
          </div>
        ))}
      </div>

      {/* WhatsApp Modal */}
      {showWhatsAppModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: 'var(--space-4)'
        }}
        onClick={() => !connectingWhatsApp && !checkingConnection && setShowWhatsAppModal(false)}
        >
          <div
            style={{
              background: 'var(--glass-bg-strong)',
              backdropFilter: 'var(--glass-blur)',
              WebkitBackdropFilter: 'var(--glass-blur)',
              border: '1px solid var(--glass-border)',
              borderRadius: 'var(--radius-2xl)',
              padding: 'var(--space-8)',
              maxWidth: '500px',
              width: '100%',
              boxShadow: 'var(--shadow-xl)'
            }}
            onClick={e => e.stopPropagation()}
          >
            <h2 style={{
              fontSize: 'var(--text-xl)',
              fontWeight: 'var(--font-weight-bold)',
              color: 'var(--text-primary)',
              marginBottom: 'var(--space-2)',
              textAlign: 'center'
            }}>
              Conectar WhatsApp
            </h2>
            <p style={{
              color: 'var(--text-secondary)',
              fontSize: 'var(--text-sm)',
              textAlign: 'center',
              marginBottom: 'var(--space-6)'
            }}>
              {qrCode ? 'Escaneie o QR Code com seu WhatsApp' : 'Digite um nome para identificar esta conexão'}
            </p>

            {!qrCode ? (
              <div>
                <label style={{
                  display: 'block',
                  fontSize: 'var(--text-sm)',
                  fontWeight: 'var(--font-weight-medium)',
                  color: 'var(--text-primary)',
                  marginBottom: 'var(--space-2)'
                }}>
                  Nome da Instância
                </label>
                <input
                  type="text"
                  value={instanceName}
                  onChange={(e) => setInstanceName(e.target.value)}
                  placeholder="Ex: WhatsApp Principal"
                  style={{
                    width: '100%',
                    padding: 'var(--space-3)',
                    fontSize: 'var(--text-base)',
                    background: 'var(--bg-tertiary)',
                    border: '1px solid var(--border-light)',
                    borderRadius: 'var(--radius-lg)',
                    outline: 'none',
                    marginBottom: 'var(--space-3)'
                  }}
                  disabled={connectingWhatsApp}
                />

                <label style={{
                  display: 'block',
                  fontSize: 'var(--text-sm)',
                  fontWeight: 'var(--font-weight-medium)',
                  color: 'var(--text-primary)',
                  marginBottom: 'var(--space-2)'
                }}>
                  URL do Webhook 🔒
                </label>
                <input
                  type="text"
                  value={webhookUrl}
                  readOnly
                  style={{
                    width: '100%',
                    padding: 'var(--space-3)',
                    fontSize: 'var(--text-sm)',
                    background: '#f5f7fa',
                    border: '1px solid var(--border-light)',
                    borderRadius: 'var(--radius-lg)',
                    outline: 'none',
                    marginBottom: 'var(--space-2)',
                    color: 'var(--text-secondary)',
                    cursor: 'not-allowed',
                    userSelect: 'all'
                  }}
                  title="URL do webhook (somente leitura - configurada automaticamente)"
                />
                <p style={{
                  fontSize: 'var(--text-xs)',
                  color: 'var(--text-tertiary)',
                  marginBottom: 'var(--space-4)'
                }}>
                  🔒 URL do webhook configurada automaticamente. Esta URL está conectada ao ngrok e não pode ser alterada.
                </p>

                <button
                  onClick={handleConnectWhatsApp}
                  disabled={connectingWhatsApp || !instanceName.trim() || !webhookUrl.trim()}
                  style={{
                    width: '100%',
                    padding: 'var(--space-3)',
                    background: 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)',
                    border: 'none',
                    borderRadius: 'var(--radius-lg)',
                    color: 'white',
                    fontSize: 'var(--text-sm)',
                    fontWeight: 'var(--font-weight-semibold)',
                    cursor: connectingWhatsApp ? 'not-allowed' : 'pointer',
                    opacity: connectingWhatsApp || !instanceName.trim() || !webhookUrl.trim() ? 0.5 : 1
                  }}
                >
                  {connectingWhatsApp ? 'Gerando QR Code...' : 'Gerar QR Code'}
                </button>
              </div>
            ) : (
              <div style={{ textAlign: 'center' }}>
                <div style={{
                  background: 'white',
                  padding: 'var(--space-4)',
                  borderRadius: 'var(--radius-xl)',
                  display: 'inline-block',
                  marginBottom: 'var(--space-4)'
                }}>
                  <img
                    src={qrCode.qrcode}
                    alt="QR Code WhatsApp"
                    style={{
                      width: '280px',
                      height: '280px',
                      display: 'block'
                    }}
                  />
                </div>
                
                {checkingConnection && (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 'var(--space-2)',
                    color: 'var(--text-secondary)',
                    fontSize: 'var(--text-sm)'
                  }}>
                    <div className="spinner" style={{ width: '16px', height: '16px' }} />
                    Aguardando conexão...
                  </div>
                )}
                
                <div style={{
                  background: 'var(--bg-tertiary)',
                  padding: 'var(--space-4)',
                  borderRadius: 'var(--radius-lg)',
                  marginTop: 'var(--space-4)'
                }}>
                  <p style={{
                    fontSize: 'var(--text-sm)',
                    color: 'var(--text-secondary)',
                    marginBottom: 'var(--space-2)'
                  }}>
                    <strong>Como conectar:</strong>
                  </p>
                  <ol style={{
                    fontSize: 'var(--text-sm)',
                    color: 'var(--text-secondary)',
                    paddingLeft: 'var(--space-4)',
                    margin: 0
                  }}>
                    <li>Abra o WhatsApp no seu celular</li>
                    <li>Toque em Menu ou Configurações</li>
                    <li>Toque em Aparelhos conectados</li>
                    <li>Toque em Conectar um aparelho</li>
                    <li>Aponte seu celular para esta tela</li>
                  </ol>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Config Modal */}
      {showConfigModal && configIntegration && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: 'var(--space-4)'
        }}
        onClick={() => setShowConfigModal(false)}
        >
          <div
            style={{
              background: 'var(--glass-bg-strong)',
              backdropFilter: 'var(--glass-blur)',
              WebkitBackdropFilter: 'var(--glass-blur)',
              border: '1px solid var(--glass-border)',
              borderRadius: 'var(--radius-2xl)',
              padding: 'var(--space-8)',
              maxWidth: '500px',
              width: '100%',
              boxShadow: 'var(--shadow-xl)'
            }}
            onClick={e => e.stopPropagation()}
          >
            <h2 style={{
              fontSize: 'var(--text-xl)',
              fontWeight: 'var(--font-weight-bold)',
              color: 'var(--text-primary)',
              marginBottom: 'var(--space-2)',
              textAlign: 'center'
            }}>
              Configurar Integração
            </h2>
            <p style={{
              color: 'var(--text-secondary)',
              fontSize: 'var(--text-sm)',
              textAlign: 'center',
              marginBottom: 'var(--space-6)'
            }}>
              {configIntegration.configuration.instance_name}
            </p>

            <label style={{
              display: 'block',
              fontSize: 'var(--text-sm)',
              fontWeight: 'var(--font-weight-medium)',
              color: 'var(--text-primary)',
              marginBottom: 'var(--space-2)'
            }}>
              Pipeline Padrão (Opcional)
            </label>
            <select
              value={selectedPipeline || ''}
              onChange={(e) => {
                const pipelineId = e.target.value ? Number(e.target.value) : null
                setSelectedPipeline(pipelineId)
                setSelectedStage(null)
                if (pipelineId) {
                  loadStages(pipelineId)
                } else {
                  setStages([])
                }
              }}
              style={{
                width: '100%',
                padding: 'var(--space-3)',
                fontSize: 'var(--text-base)',
                background: 'var(--bg-tertiary)',
                border: '1px solid var(--border-light)',
                borderRadius: 'var(--radius-lg)',
                outline: 'none',
                marginBottom: 'var(--space-3)',
                cursor: 'pointer'
              }}
            >
              <option value="">Selecione um pipeline...</option>
              {pipelines.map(pipeline => (
                <option key={pipeline.id} value={pipeline.id}>
                  {pipeline.name}
                </option>
              ))}
            </select>

            {selectedPipeline && (
              <>
                <label style={{
                  display: 'block',
                  fontSize: 'var(--text-sm)',
                  fontWeight: 'var(--font-weight-medium)',
                  color: 'var(--text-primary)',
                  marginBottom: 'var(--space-2)'
                }}>
                  Etapa Padrão (Opcional)
                </label>
                <select
                  value={selectedStage || ''}
                  onChange={(e) => setSelectedStage(e.target.value ? Number(e.target.value) : null)}
                  style={{
                    width: '100%',
                    padding: 'var(--space-3)',
                    fontSize: 'var(--text-base)',
                    background: 'var(--bg-tertiary)',
                    border: '1px solid var(--border-light)',
                    borderRadius: 'var(--radius-lg)',
                    outline: 'none',
                    marginBottom: 'var(--space-3)',
                    cursor: 'pointer'
                  }}
                  disabled={stages.length === 0}
                >
                  <option value="">Selecione uma etapa...</option>
                  {stages.map(stage => (
                    <option key={stage.id} value={stage.id}>
                      {stage.name}
                    </option>
                  ))}
                </select>
              </>
            )}

            <p style={{
              fontSize: 'var(--text-xs)',
              color: 'var(--text-tertiary)',
              marginBottom: 'var(--space-4)',
              padding: 'var(--space-2)',
              background: 'var(--bg-tertiary)',
              borderRadius: 'var(--radius-md)'
            }}>
              💡 Os leads criados via WhatsApp serão automaticamente adicionados ao pipeline e etapa selecionados.
            </p>

            <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
              <button
                onClick={() => setShowConfigModal(false)}
                style={{
                  flex: 1,
                  padding: 'var(--space-3)',
                  background: 'transparent',
                  border: '1px solid var(--border-medium)',
                  borderRadius: 'var(--radius-lg)',
                  color: 'var(--text-secondary)',
                  fontSize: 'var(--text-sm)',
                  fontWeight: 'var(--font-weight-semibold)',
                  cursor: 'pointer'
                }}
              >
                Cancelar
              </button>
              <button
                onClick={handleUpdateConfig}
                style={{
                  flex: 1,
                  padding: 'var(--space-3)',
                  background: 'var(--color-primary-500)',
                  border: 'none',
                  borderRadius: 'var(--radius-lg)',
                  color: 'white',
                  fontSize: 'var(--text-sm)',
                  fontWeight: 'var(--font-weight-semibold)',
                  cursor: 'pointer'
                }}
              >
                Salvar
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .integration-card:hover {
          box-shadow: var(--shadow-lg);
          transform: translateY(-2px);
        }
        
        .connect-btn:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(37, 211, 102, 0.3);
        }
        
        .connect-more-btn:hover {
          background: var(--color-success-50);
        }
        
        .disconnect-btn:hover {
          background: var(--color-error-50);
          border-color: var(--color-error-600);
        }
        
        .spinner {
          width: 24px;
          height: 24px;
          border: 3px solid var(--border-light);
          border-top-color: var(--color-primary-500);
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
          display: inline-block;
        }
        
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  )
}

export default Integrations
